import React from 'react';
import { Grid, List, Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/ui/Select';

export type ViewMode = 'grid' | 'list';
export type SeverityFilter = 'all' | 'critical' | 'high' | 'medium';

interface ThreatProfileControlsProps {
    searchQuery: string;
    onSearchChange: (query: string) => void;
    severityFilter: SeverityFilter;
    onSeverityFilterChange: (severity: SeverityFilter) => void;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
}

const ThreatProfileControls: React.FC<ThreatProfileControlsProps> = ({
    searchQuery,
    onSearchChange,
    severityFilter,
    onSeverityFilterChange,
    viewMode,
    onViewModeChange,
}) => {
    return (
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-4 bg-[#050505] rounded-lg border border-[#1A1D24] font-mono">

            {/* Search Matrix */}
            <div className="flex-1 max-w-xl relative group">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-slate-500 group-focus-within:text-[#00FFFF] transition-colors" />
                </div>
                <Input
                    type="search"
                    placeholder="Search by name, alias, or ID..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 h-11 bg-[#0A0B0E] border-[#1A1D24] text-[#00FFFF] placeholder:text-slate-600 text-[11px] tracking-[0.1em] uppercase focus:border-[#00FFFF] focus:ring-0 transition-all"
                />
            </div>

            {/* Parameter Filters */}
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                        <SlidersHorizontal className="w-3 h-3" /> Severity_Filter:
                    </span>
                    <Select
                        value={severityFilter}
                        onValueChange={(value) => onSeverityFilterChange(value as SeverityFilter)}
                    >
                        <SelectTrigger className="w-44 h-11 bg-[#0A0B0E] border-[#1A1D24] text-slate-300 text-[11px] uppercase tracking-wider hover:border-slate-500 transition-all focus:ring-0">
                            <SelectValue placeholder="All Severities" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0B0E] border-[#1A1D24] shadow-2xl">
                            <SelectItem value="all" className="focus:bg-[#12151C] focus:text-white text-[11px] uppercase cursor-pointer">All Severities</SelectItem>
                            <SelectItem value="critical" className="focus:bg-[#12151C] focus:text-[#FF3333] text-[11px] uppercase cursor-pointer">Critical_Lvl</SelectItem>
                            <SelectItem value="high" className="focus:bg-[#12151C] focus:text-[#F97316] text-[11px] uppercase cursor-pointer">High_Lvl</SelectItem>
                            <SelectItem value="medium" className="focus:bg-[#12151C] focus:text-[#EAB308] text-[11px] uppercase cursor-pointer">Medium_Lvl</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="h-8 w-[1px] bg-[#1A1D24] hidden md:block"></div>

                {/* View Mode Toggle */}
                <div className="flex bg-[#0A0B0E] rounded-md p-1 border border-[#1A1D24] shadow-inner">
                    <button
                        onClick={() => onViewModeChange('grid')}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest transition-all ${viewMode === 'grid'
                            ? 'bg-[#00FFFF] text-black shadow-[0_0_10px_rgba(0,255,255,0.4)]'
                            : 'text-slate-500 hover:text-white hover:bg-[#12151C]'
                            }`}
                        title="Grid View"
                    >
                        <Grid className="w-3.5 h-3.5" />
                        Grid
                    </button>
                    <button
                        onClick={() => onViewModeChange('list')}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest transition-all ${viewMode === 'list'
                            ? 'bg-[#00FFFF] text-black shadow-[0_0_10px_rgba(0,255,255,0.4)]'
                            : 'text-slate-500 hover:text-white hover:bg-[#12151C]'
                            }`}
                        title="List View"
                    >
                        <List className="w-3.5 h-3.5" />
                        List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ThreatProfileControls;